import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useAuth } from "./AuthContext";
import { getWallet } from "../components/billing/billingApi";
import { isBillingAdmin, isCreditBased, isOwnWallet, parseRoleList } from "../components/billing/billingFormat";

const BillingContext = createContext({
    loading: false,
    wallet: null,
    isCreditBased: false,
    canManageBilling: false,
    refresh: async () => {},
});

const readRoles = () => parseRoleList(Cookies.get("userRole"));

export const BillingProvider = ({ children }) => {
    const { isLoggedIn, authChecked } = useAuth();
    const [loading, setLoading] = useState(false);
    const [wallet, setWallet] = useState(null);

    const refresh = useCallback(async () => {
        if (!Cookies.get("token")) {
            setWallet(null);
            return null;
        }
        setLoading(true);
        try {
            const next = await getWallet();
            setWallet(next);
            return next;
        } catch {
            setWallet(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!authChecked) return undefined;
        if (!isLoggedIn) {
            setWallet(null);
            return undefined;
        }
        refresh();
        return undefined;
    }, [authChecked, isLoggedIn, refresh]);

    const value = useMemo(() => {
        const credit = isCreditBased(wallet?.billing_type);
        const ownWallet = isOwnWallet(wallet);
        return {
            loading,
            wallet,
            isCreditBased: credit,
            canManageBilling: credit && ownWallet && isBillingAdmin(readRoles()),
            refresh,
        };
    }, [loading, wallet, refresh]);

    return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};

export const useBilling = () => useContext(BillingContext);
